import React, { useEffect, useState } from 'react';
import {
  useIntl,
  IntlProvider,
  FormattedNumberParts,
  FormattedMessage,
} from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import ReactHtmlParser from 'react-html-parser';
import { StaticImage } from 'gatsby-plugin-image';
import { orderBy, uniqBy, uniq } from 'lodash';

import isKiosk from '../../../utils/isKiosk';
import Image, { ImageLoadingType } from '../../UI/Image/Image';
import AccessoryPromotions from '../../AccessoriesComponents/AccessoryPromotions/AccessoryPromotions';
import { getLowestMonthlyPrice } from '../../../utils/getLowestMonthlyPrice';

import {
  Card,
  Manufacturer,
  Product,
  ColourWrapper,
  Colour,
  ImageWrapper,
  CardFooter,
  From,
  PerMonth,
  ValueWrapper,
  Dollar,
  Points,
} from './styles';
import MissingProductImage from './MissingProductImage';

interface Props {
  data: Record<string, any>;
  className?: string;
  type?: string;
  section?: string;
}

const ProductCard = ({
  data,
  className = '',
  type = 'phone',
  section = '',
}: Props) => {
  const [colourList, setColourList] = useState<any[]>([]);

  const { locale } = useIntl();
  const {
    displayName,
    urlSlug,
    featuredPhoneFrontImage,
    phoneVariants,
    manufacturerName,
    heroImageLink,
    variants,
  } = data;

  const getProductVariants = () => isPhone ? phoneVariants : variants;
  const getProductImage = () => isPhone
    ? featuredPhoneFrontImage?.asset?.localImage?.publicURL
    : heroImageLink;
  const getProductUrl = () => isPhone ? 'phones' : 'accessories/accessory';
  const getBrandName = () => isPhone
    ? phoneVariants?.[0]?.phoneBrand?.displayName
    : manufacturerName;
  const getTypeName = () => isPhone ? '' : data?.type?.value;
  const getSessionStorageName = () => isPhone ? 'phoneSelected' : 'accessorySelected';
  const getCurrencyFieldName = () => isPhone ? 'monthlyPrice' : 'price';
  const getCurrencyActiveField = () => isPhone ? 'isPhoneVariantActive' : 'isActive';

  // Consolidate variable names between phones and accessories
  const isPhone = type === 'phone';
  const productVariants = getProductVariants();
  const productImage = getProductImage();
  const productUrl = getProductUrl();
  const brandName = getBrandName();
  const typeName = getTypeName();
  const sessionStorageName = getSessionStorageName();

  // Card labels (from locales/ en/fr.json)
  const noColourLabel = <FormattedMessage id="NoColour" />;
  const fromLabel = <FormattedMessage id="From" />;
  const perMonthLabel = <FormattedMessage id="PerMonth" />;
  const earnUpToLabel = <FormattedMessage id="EarnUpTo" />;
  const variantLengthLabel = phoneVariants
    ? uniq(productVariants?.map(({ monthlyPrice }) => monthlyPrice)).length
        > 1 && <From>{fromLabel}</From>
    : uniq(productVariants?.map(({ price }) => price)).length > 1 && (
    <From>{fromLabel}</From>
    );

  // Currency formatting
  const currencyFormat = getLowestMonthlyPrice({
    variants: productVariants,
    locale,
    fieldName: getCurrencyFieldName(),
    activeField: getCurrencyActiveField(),
  });

  // Creating List of unique colours in ascending price of variants
  useEffect(() => {
    let uniqueColoursList;
    if (isPhone) {
      uniqueColoursList = uniqBy(
        orderBy(phoneVariants, ['monthlyPrice'], ['asc'])
          .filter(({ phoneColour }) => phoneColour)
          .filter(({ phoneColour }) => phoneColour.displayName)
          .map((item) => item.phoneColour),
        (item) => item.displayName,
      );
    } else {
      uniqueColoursList = uniqBy(
        orderBy(variants, ['price'], ['asc'])
          .filter(({ accessoryColour }) => accessoryColour)
          .filter(({ accessoryColour }) => accessoryColour.displayName)
          .map((item) => item.accessoryColour),
        (item) => item.displayName,
      );
    }
    setColourList(uniqueColoursList);
  }, [phoneVariants, isPhone, variants]);

  return (
    <Card
      to={`/${locale}/${productUrl}/${urlSlug}`}
      aria-label={urlSlug}
      className={className}
      onClick={() => {
        window.sessionStorage.setItem(sessionStorageName, urlSlug);
      }}
      data-section={section}
      data-cta-id={ReactHtmlParser(displayName)}
    >
      <ImageWrapper className={`view-mobile ${type}`}>
        {/* eslint-disable no-nested-ternary */}
        {productImage && productImage !== 'null' ? (
          <>
            {featuredPhoneFrontImage && (
              <Image
                loading={ImageLoadingType.Lazy}
                image={featuredPhoneFrontImage}
                isImageDecorative
              />
            )}
            {heroImageLink && (
              <img
                loading={ImageLoadingType.Lazy}
                src={productImage}
                alt=""
              />
            )}
          </>
        )
          : (
            <MissingProductImage loading={ImageLoadingType.Lazy} />
          )}
      </ImageWrapper>

      <AccessoryPromotions
        brand={brandName}
        type={typeName}
        state="banner"
      />
      {brandName && (
        <Manufacturer data-testid="manufacturer-title">
          {ReactHtmlParser(brandName)}
        </Manufacturer>
      )}
      {displayName && (
        <Product data-testid="product-desc">
          {ReactHtmlParser(displayName)}
        </Product>
      )}
      {productVariants ? (
        <ColourWrapper>
          {colourList.map((items) => items.hexValue || items.assetUrl ? (
            <Colour
              key={uuidv4()}
              title={items.displayName}
              style={{
                background: phoneVariants
                  ? `#${items.hexValue}`
                  : items.hexValue,
                backgroundImage: `url(${items.assetUrl})`,
              }}
            />
          ) : (
            <Colour
              key={uuidv4()}
              className="default"
              title={noColourLabel}
            />
          ))}
          {colourList?.length > 5 && <Colour className="extra">+</Colour>}
        </ColourWrapper>
      ) : (
        <Colour className="default" title={noColourLabel} />
      )}
      <ImageWrapper className={`view-desktop ${type}`}>
        {/* eslint-disable no-nested-ternary */}
        {productImage && productImage !== 'null' ? (
          <>
            {featuredPhoneFrontImage && (
              <Image
                image={featuredPhoneFrontImage}
                loading={ImageLoadingType.Lazy}
              />
            )}
            {heroImageLink && (
              <img
                src={productImage}
                loading={ImageLoadingType.Lazy}
                alt=""
              />
            )}
          </>
        )
          : (
            <MissingProductImage loading={ImageLoadingType.Lazy} />
          )}
      </ImageWrapper>

      {productVariants?.length > 0 && (
        <CardFooter>
          <ValueWrapper className={className}>
            {currencyFormat && (
              <div data-testid="product-card-price">
                {variantLengthLabel}
                <Dollar>{currencyFormat}</Dollar>
                {isPhone && <PerMonth>{perMonthLabel}</PerMonth>}
              </div>
            )}

            {productVariants[0].pcoPoints > 0 && (
              <Points isKiosk={isKiosk}>
                <div>
                  <span className="text">{earnUpToLabel}</span>
                  <IntlProvider locale={`${locale}-CA`}>
                    <span>
                      <FormattedNumberParts
                        value={productVariants[0].pcoPoints}
                      >
                        {(parts) => parts.map((part) => part.value)}
                      </FormattedNumberParts>
                      <StaticImage
                        className="pts"
                        src="../../../images/pts.svg"
                        alt="Points"
                        aria-label="Points"
                      />
                    </span>
                  </IntlProvider>
                </div>
              </Points>
            )}
          </ValueWrapper>
        </CardFooter>
      )}
    </Card>
  );
};

export default ProductCard;
